<template>
  <div v-if="error">
    <server-error/>
  </div>
  <div v-else class="mortgage-results-page">
    <dynamic-component-viewer
      ref="dynamicComponentViewer"
      :component-name="componentName"
      :extra-params="extraParams"
      @userInteraction="handleUserInteraction"
      @component-loaded="handleComponentLoaded"
      :mountOptions="mountOptions"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Segment from '@/Segment';
import DynamicComponentViewer from '@/components/DynamicComponentViewer.vue';
import consts from '@/store/utilities/constants';
import ServerError from '@/pages/general-errors/ServerError.vue';

export default {
  name: 'MortgageContentPage',
  components: {
    DynamicComponentViewer,
    ServerError,
  },
  data() {
    return {
      componentName: consts.DYNAMIC_COMPONENT_NAMES.MORTGAGE_RESULTS,
      userSetMonthlyPay: false,
      mortgageTotalAmount: undefined,
      monthlyPayViewOnly: undefined,
      monthlyPayNumeric: 0,
      mortgageYears: undefined,
      mortgageAmount: undefined,
      showMonthlyPayError: false,
      showInitialCapitalError: false,
      isLoading: false,
      mountOptions: {
        initialPageIndex: 1,
      },
    };
  },
  computed: {
    ...mapState('mortgages', ['error']),
    extraParams() {
      return {
        mortgageYears: this.mortgageYears,
        monthlyPay: this.monthlyPayViewOnly,
        hasMonthlyPay: this.monthlyPayNumeric > 0,
        mortgageTotalAmount: this.mortgageTotalAmount,
        mortgageAmount: this.mortgageAmount,
        isLoading: this.isLoading,
      };
    },
  },
  async created() {
    Segment.trackUserInteraction('MortgageResultsPage_Entered');

    this.setAppHeaderProps({
      showHeader: true,
    });
  },
  methods: {
    ...mapActions('appHeader', ['setAppHeaderProps']),
    ...mapActions('mortgages', ['getOptimizedMortgage']),
    roundToMillions(number) {
      if (!number || Number.isNaN(number)) {
        return 0;
      }
      return Math.round((number / 1000000) * 100) / 100;
    },
    async handleComponentLoaded() {
      const { monthlyPay, maxYears, mortgagePurpose, initialCapital } = this.$route.query;
      if (initialCapital) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal('initialCapital', parseInt(initialCapital, 10));
        this.mountOptions.currentPageIndex = 1;
      }
      if (mortgagePurpose) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal('mortgagePurpose', mortgagePurpose);
        if (this.mountOptions.currentPageIndex === 1) {
          this.mountOptions.currentPageIndex = 2;
        }
      }
      if (maxYears || this.mortgageYears) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal('maxYears', maxYears ? parseInt(maxYears, 10) : this.mortgageYears);
      }
      if (monthlyPay || this.monthlyPayViewOnly) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal(
          'monthlyPay',
          monthlyPay || parseInt(this.monthlyPayViewOnly.replace(',', ''), 10),
        );
      }
      if (this.showMonthlyPayError) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal('showMonthlyPayError', this.showMonthlyPayError);
      }
      if (this.showInitialCapitalError) {
        this.$refs.dynamicComponentViewer.setStateVariableByNameLocal('showInitialCapitalError', this.showInitialCapitalError);
      }

      if (initialCapital && mortgagePurpose && !this.isLoading && !this.mortgageTotalAmount) {
        await this.triggerGetOptimizedMortgage();
      }
    },
    updateQueryParam(paramName, value) {
      const query = { ...this.$route.query };
      if (value) {
        query[paramName] = value;
      } else {
        delete query[paramName];
      }
      this.$router.replace({ query }).catch(() => {});
    },
    async handleUserInteraction(event) {
      switch (event.eventName) {
      case 'setInitialCapital':
        this.handleSetInitialCapital();
        this.$refs.dynamicComponentViewer.nextPage();
        break;
      case 'calcMortgage':
        await this.triggerGetOptimizedMortgage();
        break;
      case 'InputValueChanged':
        if (event.variableName === 'mortgagePurpose') {
          this.handleSetMortgagePurpose();
          if (event.widgetType === 'single-choice-input') {
            this.triggerGetOptimizedMortgage();
          }
        }
        if (event.variableName === 'maxYears') {
          this.handleSetMaxYears();
        }
        if (event.variableName === 'initialCapital') {
          this.handleSetInitialCapital();
        }
        if (event.variableName === 'monthlyPay') {
          this.handleSetMonthlyPay(true);
        }
        break;
      default:
        break;
      }
    },
    handleSetInitialCapital() {
      const initialCapital = this.$refs.dynamicComponentViewer.getStateVariableByNameLocal('initialCapital');
      this.updateQueryParam('initialCapital', initialCapital);
    },
    handleSetMortgagePurpose() {
      const mortgagePurpose = this.$refs.dynamicComponentViewer.getStateVariableByNameLocal('mortgagePurpose');
      this.updateQueryParam('mortgagePurpose', mortgagePurpose);
    },
    handleSetMaxYears() {
      const maxYears = this.$refs.dynamicComponentViewer.getStateVariableByNameLocal('maxYears');
      this.updateQueryParam('maxYears', maxYears);
    },
    handleSetMonthlyPay(setByUser = false) {
      if (setByUser) {
        this.userSetMonthlyPay = true;
        this.updateQueryParam('monthlyPay', monthlyPay);
      }
      const monthlyPay = this.$refs.dynamicComponentViewer.getStateVariableByNameLocal('monthlyPay');
      this.updateQueryParam('monthlyPay', monthlyPay);
    },
    getMortgageYears(optimizedMortgage) {
      if (!optimizedMortgage.optimized_mix) {
        return 0;
      }
      return Math.max(...optimizedMortgage.optimized_mix.map(mix => mix.num_months)) / 12;
    },
    async triggerGetOptimizedMortgage() {
      const { monthlyPay, maxYears, mortgagePurpose, initialCapital } = this.$refs.dynamicComponentViewer.getStateVariables();
      this.isLoading = true;
      this.$refs.dynamicComponentViewer.reloadComponent(this.extraParams);

      const optimizedMortgage = await this.getOptimizedMortgage({ monthlyPay, maxYears, mortgagePurpose, initialCapital });
      this.handleOptimizedMortgageResult(optimizedMortgage);
      this.isLoading = false;
      this.$refs.dynamicComponentViewer.reloadComponent(this.extraParams);
    },
    handleOptimizedMortgageResult(optimizedMortgage) {
      const { monthlyPay, initialCapital } = this.$refs.dynamicComponentViewer.getStateVariables();

      this.mortgageTotalAmount = this.roundToMillions(optimizedMortgage.amount + parseInt(initialCapital, 10));
      this.mortgageAmount = this.roundToMillions(optimizedMortgage.amount);
      this.monthlyPayNumeric = this.userSetMonthlyPay ? monthlyPay : optimizedMortgage.monthly_payment;
      this.monthlyPayViewOnly = this.userSetMonthlyPay ? monthlyPay?.toLocaleString() : optimizedMortgage.monthly_payment.toLocaleString();
      this.showMonthlyPayError = optimizedMortgage.monthlyPayExceedsBounds;
      this.showInitialCapitalError = optimizedMortgage.amount === optimizedMortgage.max_possible_amount_by_external_amount_only;
      this.mortgageYears = this.getMortgageYears(optimizedMortgage);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@riseupil/base-ui/src/scss/riseup-colors";
@import "~@riseupil/base-ui/src/scss/spacings";

.mortgage-results-page {
  height: 100%;
  background: $riseup_white;
  flex-grow: 1;

  .header {
    display: flex;
    justify-content: center;
    border-radius: 0px 0px 20px 20px;
    background-color: $riseup_light_blue;
  }
}
</style>
